import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import LandingLayout from "../landing/views/LandingLayout/LandingLayout";
import DashboardLayout from "../components/layout/DashboardLayout/DashboardLayout";
import ProtectedRoute from "../components/auth/ProtectedRoute.jsx";
import RouteErrorBoundary from "../components/common/RouteErrorBoundary/RouteErrorBoundary";

// Lazy load landing pages
const HomeView = lazy(() => import("../landing/home/view/HomeView"));
const FeaturesView = lazy(
  () => import("../landing/features/view/FeaturesView"),
);
const PricingView = lazy(() => import("../landing/pricing/view/PricingView"));
const AboutView = lazy(() => import("../landing/about/view/AboutView"));
const ContactView = lazy(() => import("../landing/contact/view/ContactView"));
import PolicyView from "../landing/policy/view/PolicyView";
const LoginContainer = lazy(
  () => import("../landing/auth/login/LoginContainer.jsx"),
);
const SignupContainer = lazy(
  () => import("../landing/auth/signup/SignupContainer"),
);

// Lazy load Super Admin pages
const SuperAdminDashboardView = lazy(
  () => import("../superAdmin/dashboard/view/SuperAdminDashboardView"),
);
const BusinessListView = lazy(
  () => import("../superAdmin/businesses/businessList/view/BusinessListView"),
);
const SubscriptionListView = lazy(
  () =>
    import("../superAdmin/subscriptions/subscriptionList/view/SubscriptionListView"),
);
const UserListView = lazy(
  () => import("../superAdmin/users/userList/view/UserListView"),
);
const OrderListView = lazy(
  () => import("../superAdmin/orders/orderList/view/OrderListView"),
);
const SuperAdminRevenueView = lazy(
  () => import("../superAdmin/revenue/view/RevenueView"),
);

// Lazy load Client CRM pages
const ClientDashboardView = lazy(
  () => import("../client/dashboard/admin/AdminDashboardContainer"),
);
const ClientListView = lazy(
  () => import("../client/clients/clientList/view/ClientListView"),
);
const ClientDetailView = lazy(
  () => import("../client/clients/clientDetail/ClientDetailContainer"),
);
// Sessions (formerly bookings)
const SessionListView = lazy(
  () => import("../client/sessions/SessionsContainer.jsx"),
);

// Fitness workflow modules
const AttendanceView = lazy(
  () => import("../client/attendance/AttendanceContainer.jsx"),
);
const NotificationsView = lazy(
  () => import("../client/notifications/NotificationsContainer.jsx"),
);
const RemindersView = lazy(
  () => import("../client/reminders/RemindersContainer.jsx"),
);
import FeedbackDetailView from "../client/feedback/FeedbackDetailView";
const FeedbackView = lazy(
  () => import("../client/feedback/FeedbackContainer.jsx"),
);
const ProgramsView = lazy(
  () => import("../client/programs/ProgramsContainer.jsx"),
);
const NutritionView = lazy(
  () => import("../client/nutrition/NutritionContainer.jsx"),
);
const OrderListViewClient = lazy(
  () => import("../client/orders/OrderContainer.jsx"),
);

const ReportsView = lazy(
  () => import("../client/reports/ReportsContainer.jsx"),
);
const SettingsView = lazy(() => import("../client/settings/SettingsContainer"));
const ProfileView = lazy(
  () => import("../client/profile/ProfileContainer.jsx"),
);
const BusinessSelectionView = lazy(
  () => import("../client/business-selection/BusinessSelectionContainer.jsx"),
);
const ClientRevenueView = lazy(
  () => import("../client/revenue/RevenueContainer"),
);
const SubscriptionViewClient = lazy(
  () => import("../client/subscription/SubscriptionContainer"),
);
const VerifyOtpContainer = lazy(
  () => import("../landing/auth/verify-otp/VerifyOtpContainer.jsx"),
);
const TrainerListView = lazy(
  () => import("../client/trainers/trainerList/view/TrainerListView"),
);
const TrainerFormView = lazy(
  () => import("../client/trainers/trainerForm/view/TrainerFormView"),
);
const TrainerDetailView = lazy(
  () => import("../client/trainers/trainerDetail/TrainerDetailContainer"),
);
const ForgotPasswordContainer = lazy(
  () => import("../landing/auth/forgot-password/ForgotPasswordContainer.jsx"),
);
const PublicJoinForm = lazy(
  () => import("../client/public/PublicJoinForm.jsx")
);
const UserFeedbackForm = lazy(
  () => import("../client/feedback/UserFeedbackForm.jsx")
);
const FeedbackListView = lazy(
  () => import("../client/feedback/list/FeedbackListContainer.jsx")
);

// Fitness workflow modules
const ExerciseLibraryView = lazy(
  () => import("../client/fitness/exercises/ExerciseLibraryContainer"),
);
const WorkoutPlansView = lazy(
  () => import("../client/fitness/workouts/WorkoutPlansContainer"),
);
const ScheduleView = lazy(() => import("../client/scheduling/ScheduleView"));
const LeadsContainer = lazy(() => import("../client/crm/leads/LeadsContainer"));
const InventoryContainer = lazy(
  () => import("../client/inventory/InventoryContainer"),
);
const POSContainer = lazy(() => import("../client/pos/POSContainer"));
const ExecutiveContainer = lazy(
  () => import("../client/executive/ExecutiveContainer"),
);
const PostContainer = lazy(() => import("../client/marketing/PostContainer"));
const PosterTemplateView = lazy(
  () => import("../superAdmin/marketing/PosterTemplateView"),
);

import PageLoader from "../components/ui/PageLoader/PageLoader";

const SuspenseFallback = () => <PageLoader text="Loading BizAura..." fullScreen={true} />;

import ChatBot from "../components/common/ChatBot/ChatBot.jsx";

// Landing layout wrapper
const LandingWrapper = () => (
  <LandingLayout>
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
    <ChatBot />
  </LandingLayout>
);

// Super Admin layout wrapper
const SuperAdminWrapper = () => (
  <DashboardLayout
    variant="admin"
    user={{ name: "Super Admin", role: "Administrator" }}
  >
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

import SubscriptionGuard from "../components/auth/SubscriptionGuard.jsx";

// Client layout wrapper - Protected by authentication
const ClientWrapper = () => {
  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const businessType = localStorage.getItem("businessType") || "fitnesh";

  const currentUser = {
    id: userInfo?.memberId || "u1",
    name: userInfo?.name || "User",
    role: "Business Owner",
    businessType: businessType,
  };

  return (
    <ProtectedRoute>
      <SubscriptionGuard>
        <DashboardLayout variant="client" user={currentUser}>
          <Suspense fallback={<PageLoader />}>
            <Outlet context={{ user: currentUser }} />
          </Suspense>
        </DashboardLayout>
      </SubscriptionGuard>
    </ProtectedRoute>
  );
};

export const router = createBrowserRouter([
  // Landing routes
  {
    path: "/",
    element: <LandingWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomeView /> },
      { path: "features", element: <FeaturesView /> },
      { path: "pricing", element: <PricingView /> },
      { path: "about", element: <AboutView /> },
      { path: "contact", element: <ContactView /> },
      { path: "policy/:type", element: <PolicyView /> },
      { path: "login", element: <LoginContainer /> },
      { path: "signup", element: <SignupContainer /> },
      { path: "verify-otp", element: <VerifyOtpContainer /> },
      { path: "forgot-password", element: <ForgotPasswordContainer /> },
    ],
  },
  // Public join route (standalone)
  {
    path: "/join/:code",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicJoinForm />
      </Suspense>
    ),
  },
  // Public feedback form route
  {
    path: "/submit-feedback",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserFeedbackForm />
      </Suspense>
    ),
  },
  {
    path: "/submit-feedback/:code",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserFeedbackForm />
      </Suspense>
    ),
  },
  // Super Admin routes
  {
    path: "/admin",
    element: <SuperAdminWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: "dashboard", element: <SuperAdminDashboardView /> },
      { path: "businesses", element: <BusinessListView /> },
      { path: "subscriptions", element: <SubscriptionListView /> },
      { path: "orders", element: <OrderListView /> },
      { path: "users", element: <UserListView /> },
      { path: "revenue", element: <SuperAdminRevenueView /> },
      { path: "reports", element: <ReportsView /> },
      { path: "settings", element: <SettingsView /> },
      { path: "poster-templates", element: <PosterTemplateView /> },
    ],
  },
  // Client CRM routes - Moved to /app/*
  {
    path: "/app",
    element: <ClientWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/app/dashboard" replace /> },
      { path: "dashboard", element: <ClientDashboardView /> },
      { path: "clients", element: <ClientListView /> },
      { path: "clients/:clientId", element: <ClientDetailView /> },
      // Sessions (renamed from bookings - support both paths)
      { path: "sessions", element: <SessionListView /> },
      { path: "bookings", element: <Navigate to="/app/sessions" replace /> },
      { path: "orders", element: <OrderListViewClient /> },
      // Fitness workflow modules
      { path: "attendance", element: <AttendanceView /> },
      { path: "notifications", element: <NotificationsView /> },
      { path: "reminders", element: <RemindersView /> },
      { path: "feedback", element: <FeedbackView /> },
      { path: "user-feedback-list", element: <FeedbackListView /> },
      { path: "user-feedback-list/:id", element: <FeedbackDetailView /> },
      { path: "programs", element: <ProgramsView /> },
      { path: "nutrition", element: <NutritionView /> },
      { path: "revenue", element: <ClientRevenueView /> },
      { path: "reports", element: <ReportsView /> },
      { path: "settings", element: <SettingsView /> },
      { path: "profile", element: <ProfileView /> },
      { path: "subscription", element: <SubscriptionViewClient /> },
      { path: "subscriptions", element: <SubscriptionViewClient /> },
      { path: "pricing", element: <PricingView /> },
      { path: "trainers", element: <TrainerListView /> },
      { path: "trainers/:id", element: <TrainerDetailView /> },
      { path: "trainers/add", element: <TrainerFormView /> },
      { path: "trainers/edit/:id", element: <TrainerFormView /> },
      // Fitness routes
      { path: "exercises", element: <ExerciseLibraryView /> },
      { path: "exercises/:id", element: <ExerciseLibraryView /> },
      { path: "workouts", element: <WorkoutPlansView /> },
      { path: "schedule", element: <ScheduleView /> },
      { path: "leads", element: <LeadsContainer /> },
      { path: "inventory", element: <InventoryContainer /> },
      { path: "pos", element: <POSContainer /> },
      { path: "executive", element: <ExecutiveContainer /> },
      { path: "post", element: <PostContainer /> },
    ],
  },
  // Catch all redirect
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
  // Business Selection route - disabled
  // {
  //   path: "/select-business",
  //   element: (
  //     <Suspense fallback={<PageLoader />}>
  //       <BusinessSelectionView />
  //     </Suspense>
  //   ),
  // },
]);

export default router;
